import { useEffect, useState } from 'react';

import * as yup from 'yup';
import { Form, Formik } from 'formik';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FormError } from '@/components/ui/form-field';
import { PMSVendorData } from '@/types/pms.types';
import type { PmsAccountRef } from '@/components/pms/services/pmsAccount';
import { Button } from '@/components/ui/button';
import { useDispatch } from 'react-redux';
import { testVendorConnectionSyncAPI } from '../../../pms/pms-configuration/service/configurationApiService';
import { showAlert } from '../../../../slice/AlertSlice';
import { Variants } from '@/types/common';
import { Typography, typographyVariants } from '@/components/ui/typography';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import {
  type ConnectionState,
  connectedConnection,
  failedConnection,
} from '../../../pms/pms-configuration/types/connection-state';
import { FormikDirtyReporter } from '@/components/pms/vendor-settings/panels/FormikDirtyReporter';
import { SubmitRefCleanup } from '@/components/pms/vendor-settings/panels/SubmitRefCleanup';
import { integrationStatusCopy as copy } from '@/components/pms/vendor-settings/status/copy';
import type { VendorSettingsMode, SubmittedCredentials } from '@/components/pms/vendor-settings/types';

interface AppfolioConnectionProps {
  /** DV-3345: the account every PMS call here names. */
  account: PmsAccountRef;
  connected: boolean;
  setConnected: (val: boolean) => void;
  formSubmit: boolean;
  connectionState: ConnectionState;
  setConnectionState: React.Dispatch<React.SetStateAction<ConnectionState>>;
  onSubmit: (values: PMSVendorData) => void;
  redirectToHowToConfigure: (val: boolean) => void;
  configConnectionFormRef: React.MutableRefObject<(() => void) | null>;
  setVenderListData: (data: PMSVendorData) => void;
  autoSyncAssets: boolean;
  watermarkedPhotos: boolean;
  setToggleLoader: (val: boolean) => void;
  pmsSource: string;
  initialVendorData?: PMSVendorData | null;
  /** Spec §5.1: the form only exists in edit mode; read mode shows the panel's summary. */
  mode: VendorSettingsMode;
  /** Remount key so a discard clears typed credentials (spec §5.4). */
  formKey: number;
  /**
   * Credentials from a save that was rejected — the form remounts seeded from
   * them instead of blank, so the retry carries what the user pasted
   * (review finding 1). Null at every other time.
   */
  pendingCredentials: SubmittedCredentials | null;
  onDirtyChange: (dirty: boolean) => void;
}

/** Spec §5.4: key/secret are required only when nothing is stored for them. */
const buildSchema = (stored: { key: boolean; secret: boolean }) =>
  yup.object().shape({
    url: yup.string(),
    key: stored.key ? yup.string() : yup.string().required('Please enter API Key'),
    secret: stored.secret ? yup.string() : yup.string().required('Please enter API Secret'),
  });

const AppfolioConnection = ({
  account,
  connected,
  setConnected,
  formSubmit,
  setConnectionState,
  onSubmit,
  redirectToHowToConfigure,
  configConnectionFormRef,
  setVenderListData,
  autoSyncAssets,
  watermarkedPhotos,
  setToggleLoader,
  pmsSource,
  initialVendorData,
  mode,
  formKey,
  pendingCredentials,
  onDirtyChange,
}: AppfolioConnectionProps) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const [PMSVendorsData, setPMSVendorsData] = useState<PMSVendorData>({
    status: undefined,
    url: 'api',
    key: '',
    secret: '',
    autoSyncAssets,
    watermarkedPhotos
  });

  useEffect(() => {
    getPMSVendorsList();
  }, []);

  const getPMSVendorsList = async () => {
    setLoading(true);
    setToggleLoader(true);
    try {
      if (initialVendorData) {
        setPMSVendorsData(initialVendorData);
        setVenderListData(initialVendorData);
        try {
          const configResponse = await testVendorConnectionSyncAPI(
            account,
            initialVendorData,
            autoSyncAssets,
            watermarkedPhotos,
            pmsSource
          );
          if (configResponse?.data?.status === 'Success') {
            setConnected(true);
            setConnectionState(connectedConnection());
          }
        } catch (error: unknown) {
          const err = error as { response?: { status?: number } };
          if (err.response?.status) {
            setConnected(false);
            setConnectionState(
              failedConnection()
            );
            if (formSubmit) {
              dispatch(
                showAlert({
                  type: Variants.ERROR,
                  message:
                    'Connection failed.  Please check the credentials and try again.'
                })
              );
            } else {
              dispatch(
                showAlert({
                  type: Variants.ERROR,
                  message:
                    'Connection failed. Update credentials to resume syncing.'
                })
              );
            }
          }
        }
      }
    } finally {
      setLoading(false);
      setToggleLoader(false);
    }
  };

  const redirectHowToConfigDetails = () => {
    redirectToHowToConfigure(true);
  };

  // Stored credentials are never echoed back into the form: blank key/secret
  // mean "keep what is stored" (spec §2 decision 5) — except after a rejected
  // save, where the form comes back carrying exactly what the user submitted,
  // so the retry does not send blanks that merge the old credentials back in.
  const setInitValue = (): PMSVendorData => ({
    url: pendingCredentials?.url ?? PMSVendorsData?.url,
    key: pendingCredentials?.key ?? '',
    secret: pendingCredentials?.secret ?? '',
  });

  const hasStoredKey = Boolean(initialVendorData?.key);
  const hasStoredSecret = Boolean(initialVendorData?.secret);

  return (
    <>
      {/* Read mode shows the panel's summary; the on-mount credential test
          must not paint a large spinner underneath it. */}
      {loading && mode === 'edit' && (
        <div className="flex items-center justify-center py-10">
          <Spinner size="lg" />
        </div>
      )}
      {!loading && mode === 'edit' && (
        <>
          <div className="p-4">
            <Typography variant="heading-md" className="font-medium">Please fill in the details below. Refer to the </Typography>
            <Button type="button" variant="link" onClick={redirectHowToConfigDetails}>
              {' guide '}
            </Button>
            <Typography variant="heading-md" className="font-medium"> in the Integration Overview tab for step-by-step instructions.</Typography>
          </div>
          <Formik
            key={formKey}
            validationSchema={buildSchema({ key: hasStoredKey, secret: hasStoredSecret })}
            initialValues={setInitValue()}
            validateOnBlur={true}
            validateOnChange={false}
            onSubmit={async (values) => {
              onSubmit(values);
            }}
            enableReinitialize
          >
            {({ handleSubmit, handleChange: formikHandleChange, values, touched, errors }) => {
              if (configConnectionFormRef) {
                configConnectionFormRef.current = handleSubmit;
              }
              return (
                <Form
                  id="vendor-configuration"
                  noValidate
                  onSubmit={handleSubmit}
                >
                  <FormikDirtyReporter onDirtyChange={onDirtyChange} />
                  <SubmitRefCleanup
                    clear={() => {
                      if (configConnectionFormRef) configConnectionFormRef.current = null;
                    }}
                  />
                  <div className={`mb-4 px-4 ${connected ? '' : 'border-destructive border rounded-md p-3'}`}>
                    <Label htmlFor="APIKey" required={!hasStoredKey}>API Key</Label>
                    <Input id="APIKey" name="key" type="text" placeholder={hasStoredKey ? copy.editMode.pasteKey : 'Enter API Key'} value={values.key} onChange={formikHandleChange} className={touched.key && errors.key ? 'border-destructive' : ''} aria-describedby="APIKey" data-testid="APIKey_id" />
                    <FormError message={touched.key ? errors.key : undefined} />
                  </div>
                  <div className={`mb-4 px-4 ${connected ? '' : 'border-destructive border rounded-md p-3'}`}>
                    <Label htmlFor="APISecret" required={!hasStoredSecret}>API Secret</Label>
                    <Input id="APISecret" name="secret" type="password" placeholder={hasStoredSecret ? copy.editMode.pasteSecret : 'Enter API Secret'} value={values.secret} onChange={formikHandleChange} className={touched.secret && errors.secret ? 'border-destructive' : ''} aria-describedby="APISecret" data-testid="APISecret_id" />
                    <FormError message={touched.secret ? errors.secret : undefined} />
                  </div>
                  {initialVendorData && (
                    <p className={cn(typographyVariants({ variant: 'body-sm' }), 'px-4 text-muted-foreground')}>
                      {copy.editMode.maskNote}
                    </p>
                  )}
                </Form>
              );
            }}
          </Formik>
        </>
      )}
    </>
  );
};

export default AppfolioConnection;
